/**
 * Público do culto (Membresia › Presença nos cultos e a aba "Público do culto" do painel
 * do evento): o registro agregado de quantas pessoas estiveram no culto, **sem chamada
 * nominal**. Não é o check-in de quem serviu (`presence`): são finalidades diferentes.
 *
 * - Ausência de registro é "Público não registrado", nunca zero; zero é alguém que
 *   registrou zero.
 * - As crianças já fazem parte do total presencial e não são somadas de novo; o online
 *   fica separado.
 * - Membros + visitantes podem não somar o total (a contagem nem sempre é segmentada);
 *   passar do total pede confirmação, não bloqueia.
 */
import type { AudienceMethod, ScheduleEvent, ServiceAudience } from './types';

export const AUDIENCE_METHODS: { value: AudienceMethod; label: string }[] = [
  { value: 'manual', label: 'Contagem manual' },
  { value: 'estimate', label: 'Estimativa' },
  { value: 'counter', label: 'Contador na entrada' },
  { value: 'other', label: 'Outro' },
];

export function audienceMethodLabel(method: AudienceMethod): string {
  return AUDIENCE_METHODS.find((m) => m.value === method)?.label ?? method;
}

/** Limite de cada contagem (um culto não passa disso; evita erro de digitação). */
export const MAX_AUDIENCE = 100_000;
export const MAX_AUDIENCE_NOTE = 280;

/** Os campos numéricos do registro, na ordem do formulário. */
export const AUDIENCE_COUNTS = ['total', 'members', 'firstTime', 'recurring', 'kids', 'online'] as const;
export type AudienceCount = (typeof AUDIENCE_COUNTS)[number];

/** O formulário como se digita: os números em texto (`''` = não informado). */
export type AudienceDraft = Record<AudienceCount, string> & { method: AudienceMethod; note: string };

/** O que se grava: as contagens e o método, sem a auditoria (quem e quando). */
export type AudienceInput = Pick<ServiceAudience, AudienceCount | 'method' | 'note'>;

export function audienceDraftOf(record: ServiceAudience | null): AudienceDraft {
  const text = (n: number | null | undefined) => (n === null || n === undefined ? '' : String(n));

  return {
    total: text(record?.total),
    members: text(record?.members),
    firstTime: text(record?.firstTime),
    recurring: text(record?.recurring),
    kids: text(record?.kids),
    online: text(record?.online),
    method: record?.method ?? 'manual',
    note: record?.note ?? '',
  };
}

export type AudienceErrors = Partial<Record<AudienceCount | 'note', string>>;

export interface AudienceCheck {
  /** O registro pronto para gravar; `null` enquanto há erro. */
  input: AudienceInput | null;
  errors: AudienceErrors;
  /** Membros + visitantes informados (0 sem nenhum). */
  segmented: number;
  /** Membros + visitantes passam do total: salva só com confirmação. */
  segmentOver: boolean;
}

function parseCount(text: string): number | null | 'invalid' {
  const value = text.trim();

  if (!value) return null;
  if (!/^\d+$/.test(value)) return 'invalid';

  const n = Number(value);

  return n > MAX_AUDIENCE ? 'invalid' : n;
}

/** Confere o formulário: inteiros não negativos, total obrigatório, crianças dentro do total. */
export function checkAudience(draft: AudienceDraft): AudienceCheck {
  const errors: AudienceErrors = {};
  const counts = {} as Record<AudienceCount, number | null>;

  for (const key of AUDIENCE_COUNTS) {
    const n = parseCount(draft[key]);

    if (n === 'invalid') {
      errors[key] =
        `Use um número inteiro de 0 a ${MAX_AUDIENCE.toLocaleString('pt-BR')}, sem sinais ou casas decimais.`;
      counts[key] = null;
    } else {
      counts[key] = n;
    }
  }

  if (!errors.total && counts.total === null) errors.total = 'Informe o público presencial.';
  if (!errors.kids && counts.kids !== null && counts.total !== null && counts.kids > counts.total) {
    errors.kids = 'Crianças fazem parte do público presencial e não podem passar do total.';
  }

  const note = draft.note.trim();

  if (note.length > MAX_AUDIENCE_NOTE) errors.note = `Use até ${MAX_AUDIENCE_NOTE} caracteres.`;

  const segmented = (counts.members ?? 0) + (counts.firstTime ?? 0) + (counts.recurring ?? 0);
  const segmentOver = counts.total !== null && segmented > counts.total;
  const ok = Object.keys(errors).length === 0;

  return {
    input: ok ? { ...counts, total: counts.total!, method: draft.method, note } : null,
    errors,
    segmented,
    segmentOver,
  };
}

/** Por que o registro não vale (o mesmo texto do formulário); `null` quando vale. */
export function audienceInputError(input: AudienceInput): string | null {
  const draft = audienceDraftOf({
    ...input,
    recordedBy: '',
    recordedAt: 0,
    updatedBy: null,
    updatedAt: null,
  });
  const errors = checkAudience({ ...draft, note: input.note });

  return Object.values(errors.errors)[0] ?? null;
}

/** O público se registra no dia do culto ou depois. */
export function audienceOpen(event: Pick<ScheduleEvent, 'date'>, today: string): boolean {
  return event.date <= today;
}

/**
 * Grava o registro no evento: o primeiro guarda quem registrou; os seguintes mantêm o
 * registro original e anotam a edição.
 */
export function withAudience(
  event: ScheduleEvent,
  input: AudienceInput,
  by: string,
  at: number
): ScheduleEvent {
  const before = event.audience;
  const audience: ServiceAudience = {
    ...input,
    note: input.note.trim(),
    recordedBy: before?.recordedBy ?? by,
    recordedAt: before?.recordedAt ?? at,
    updatedBy: before ? by : null,
    updatedAt: before ? at : null,
  };

  return { ...event, audience };
}

/** Visitantes do culto (primeira vez + retorno); `null` sem nenhum dos dois informado. */
export function visitorsOf(audience: ServiceAudience): number | null {
  if (audience.firstTime === null && audience.recurring === null) return null;

  return (audience.firstTime ?? 0) + (audience.recurring ?? 0);
}
